import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { 
    Shield, Brain, Heart, Lock, Users, Sparkles, 
    CheckCircle2, AlertTriangle, FileText, GraduationCap
} from 'lucide-react';

// Nanouk mascot images
const NANOUK_SMART = 'https://images.unsplash.com/photo-1563889958768-0fa816b019c7?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop';

export default function TrustSafety() {
    const { language } = useLanguage();

    const content = {
        de: {
            title: 'Das BESTBUDDY Qualitäts-Versprechen',
            subtitle: '"Sicher an deiner Seite"',
            tagline: 'Nanouk hat das Herz eines Freundes, aber das Wissen von Experten.',
            sections: [
                {
                    icon: Brain,
                    title: 'Evidenzbasierte Intelligenz',
                    badge: 'Med-Gemini',
                    content: 'Nanouks Ratschläge basieren nicht auf Trends, sondern auf wissenschaftlichen Goldstandards. Unsere Wissensdatenbank wird aus validierten sportmedizinischen Studien, ernährungswissenschaftlichen Leitfäden und klinisch anerkannten Schlaf-Forschungsergebnissen gespeist.',
                    highlight: 'Jede Empfehlung wird durch spezialisierte Health-KI (Med-Gemini) auf Plausibilität geprüft.'
                },
                {
                    icon: AlertTriangle,
                    title: 'Der "Safety-First" Filter',
                    badge: 'Frühwarnsystem',
                    content: 'Nanouk erkennt seine Grenzen. Bei kritischen Datenwerten oder medizinischen Unklarheiten gibt Nanouk keine Diagnose ab, sondern fungiert als Frühwarnsystem.',
                    highlight: 'Er motiviert dich aktiv dazu, Rücksprache mit deinem Arzt zu halten und stellt dir dafür direkt den passenden Daten-Export (PDF-Report) zur Verfügung.'
                },
                {
                    icon: Users,
                    title: 'Kuratiert von Experten',
                    badge: 'Human-in-the-Loop',
                    content: 'Hinter der KI steht ein Team aus menschlichen Experten. Sportmediziner, Ernährungsberater und Schlaf-Coaches überwachen die Algorithmen von BESTBUDDY.',
                    highlight: 'Wir kombinieren das Beste aus zwei Welten: Die Geschwindigkeit und Personalisierung der KI mit der Erfahrung und Ethik menschlicher Fachleute.'
                },
                {
                    icon: Lock,
                    title: 'Absolute Datensouveränität',
                    badge: 'DSGVO & HIPAA',
                    content: 'Deine Gesundheitsdaten sind dein privatestes Gut. BESTBUDDY arbeitet nach strengsten DSGVO- und HIPAA-Standards.',
                    highlight: 'Nanouk nutzt deine Daten ausschließlich, um dein persönliches Erlebnis zu verbessern – sie werden niemals ohne deine explizite Zustimmung geteilt oder verkauft.'
                }
            ],
            disclaimer: 'BESTBUDDY ersetzt keine ärztliche Beratung. Bei gesundheitlichen Beschwerden wende dich bitte an einen Facharzt.',
            sources: 'Wissenschaftliche Quellen',
            sourcesList: [
                'Med-Gemini Health AI',
                'Sportmedizinische Leitlinien',
                'Ernährungswissenschaftliche Studien',
                'Schlafforschungs-Datenbanken'
            ]
        },
        en: {
            title: 'The BESTBUDDY Quality Promise',
            subtitle: '"Safely by Your Side"',
            tagline: 'Nanouk has the heart of a friend, but the knowledge of experts.',
            sections: [
                {
                    icon: Brain,
                    title: 'Evidence-Based Intelligence',
                    badge: 'Med-Gemini',
                    content: "Nanouk's advice is not based on trends, but on scientific gold standards. Our knowledge base is fed by validated sports medicine studies, nutritional guidelines, and clinically recognized sleep research.",
                    highlight: 'Every recommendation is checked for plausibility by specialized Health AI (Med-Gemini).'
                },
                {
                    icon: AlertTriangle,
                    title: 'The "Safety-First" Filter',
                    badge: 'Early Warning',
                    content: 'Nanouk knows his limits. With critical data values or medical uncertainties, Nanouk does not give diagnoses but acts as an early warning system.',
                    highlight: 'He actively encourages you to consult your doctor and provides you with the appropriate data export (PDF report) directly.'
                },
                {
                    icon: Users,
                    title: 'Curated by Experts',
                    badge: 'Human-in-the-Loop',
                    content: "Behind the AI is a team of human experts. Sports physicians, nutritionists, and sleep coaches monitor BESTBUDDY's algorithms.",
                    highlight: 'We combine the best of both worlds: The speed and personalization of AI with the experience and ethics of human professionals.'
                },
                {
                    icon: Lock,
                    title: 'Absolute Data Sovereignty',
                    badge: 'GDPR & HIPAA',
                    content: 'Your health data is your most private asset. BESTBUDDY operates according to the strictest GDPR and HIPAA standards.',
                    highlight: 'Nanouk only uses your data to improve your personal experience – it is never shared or sold without your explicit consent.'
                }
            ],
            disclaimer: 'BESTBUDDY does not replace medical advice. For health concerns, please consult a healthcare professional.',
            sources: 'Scientific Sources',
            sourcesList: [
                'Med-Gemini Health AI',
                'Sports Medicine Guidelines',
                'Nutritional Science Studies',
                'Sleep Research Databases'
            ]
        }
    };

    const t = content[language] || content.de;

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="trust-safety-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-6"
            >
                {/* Header with Smart Nanouk */}
                <div className="text-center mb-8">
                    <div className="w-32 h-32 mx-auto mb-4 rounded-3xl overflow-hidden shadow-xl">
                        <img 
                            src={NANOUK_SMART} 
                            alt="Nanouk - Der schlaue BestBuddy"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold flex items-center justify-center gap-3">
                        <Shield className="w-8 h-8 text-cyan-500" />
                        {t.title}
                    </h1>
                    <p className="text-xl text-cyan-600 dark:text-primary mt-2 font-medium">
                        {t.subtitle}
                    </p>
                    <p className="text-muted-foreground mt-3 italic">
                        "{t.tagline}"
                    </p>
                </div>

                {/* Quality Promise Cards */}
                <div className="grid gap-6">
                    {t.sections.map((section, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <Card className="overflow-hidden">
                                <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/30 dark:to-blue-950/30 border-b">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-lg flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center">
                                                <section.icon className="w-5 h-5 text-cyan-600 dark:text-primary" />
                                            </div>
                                            {section.title}
                                        </CardTitle>
                                        <Badge className="bg-primary">{section.badge}</Badge>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-6">
                                    <p className="text-muted-foreground mb-4">
                                        {section.content}
                                    </p>
                                    <div className="flex items-start gap-2 p-4 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-green-800 dark:text-green-200 font-medium">
                                            {section.highlight}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* Scientific Sources */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <GraduationCap className="w-5 h-5 text-cyan-500" />
                            {t.sources}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {t.sourcesList.map((source, index) => (
                                <Badge key={index} variant="outline" className="text-sm py-1 px-3">
                                    {source}
                                </Badge>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Disclaimer */}
                <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                    <CardContent className="p-4 flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                            {t.disclaimer}
                        </p>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}
